import React from "react";

import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { ButtonBase, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { MRT_Cell, MRT_Column, MRT_Row, MRT_TableInstance } from "material-react-table";
import { useReportsQueriesSelector } from "store";
import { ReportPredictionTableCell, ReportPredictionTableRow } from "store/types/reports";
import { formatCurrency } from "utils/strings";

import { currencySign } from "../../../../constants";
import { UpdateSettingCell } from "./PredictionTable";

interface Props {
  cell: MRT_Cell<ReportPredictionTableRow, unknown>;
  column: MRT_Column<ReportPredictionTableRow, unknown>;
  renderedCellValue: React.ReactNode;
  row: MRT_Row<ReportPredictionTableRow>;
  rowRef?: React.RefObject<HTMLTableRowElement | null>;
  staticColumnIndex?: number;
  staticRowIndex?: number;
  table: MRT_TableInstance<ReportPredictionTableRow>;

  cellUpdatingKey: string | null;
  rowUpdatingId: number | null;
  updateSettingCell: UpdateSettingCell;
}

export const ColumnCell: React.FC<Props> = ({ column, row, cellUpdatingKey, rowUpdatingId, updateSettingCell }) => {
  const rowData = row.original;
  const cellData = rowData[column.id] as ReportPredictionTableCell;

  const isUserModified = Boolean(cellData?.user_id);
  const { currency } = useReportsQueriesSelector();

  const currencySymbol = currencySign[currency] || "";

  const displayValue = cellData?.value ? formatCurrency({ value: Number(cellData.value), currencySymbol }) : null;
  // const tooltipValue = cellData?.value ? formatCurrency({ value: Number(cellData.value), currencySymbol }) : null;

  const handleCellClick = ({
    columnKey,
    ...rest
  }: {
    currentValue: string;
    user_id: number | null;
    anchorEl: HTMLButtonElement;
    columnKey: string;
  }) => {
    if (!rowData.id) return;
    const dealValues: Array<{ id: number; value: number; user_id: number | null }> = [];

    const queryDeals = (row: ReportPredictionTableRow) => {
      if (!row.id) return;
      if (row.type === "deal") {
        const cellData = row[columnKey] as { user_id: number | null; value: string | null };
        dealValues.push({ id: row.id, value: Number(cellData.value), user_id: cellData.user_id });
      } else {
        row.children?.forEach((child) => queryDeals(child));
      }
    };

    if (rowData.type === "deal" && rowData.id) {
      dealValues.push({ id: rowData.id, value: Number(rest.currentValue), user_id: rest.user_id });
    } else {
      rowData.children?.forEach((child) => queryDeals(child));
    }

    updateSettingCell({
      id: rowData.id,
      type: rowData.type,
      columnKey,
      cellData: rowData[columnKey] as ReportPredictionTableCell,
      anchorEl: rest.anchorEl,
      dealValues,
      assetClassName: rowData.name
    });
  };

  return (
    <Stack
      sx={{
        height: "100%",
        maxWidth: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
        ...(isUserModified && {
          backgroundColor: "#EDFDF7"
        }),
        ...(row.id &&
          rowData.type &&
          cellUpdatingKey !== `${column.id}_${rowData.id}` && {
            "&:hover": {
              ".MuiStack-root": {
                backgroundColor: "#DBE8F5"
              },
              "& .edit-icon": {
                opacity: 1
              }
            }
          })
      }}
    >
      {cellUpdatingKey === `${column.id}_${rowData.id}` ? (
        <Stack sx={{ width: "100%", height: "100%", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress sx={{ margin: "0 auto" }} size={20} />
        </Stack>
      ) : (
        <Tooltip title={displayValue} placement="top">
          <ButtonBase
            disabled={Boolean(cellUpdatingKey || rowUpdatingId)}
            onClick={(e) =>
              handleCellClick({
                currentValue: cellData?.value || "",
                user_id: cellData?.user_id,
                anchorEl: e.currentTarget,
                columnKey: column.id
              })
            }
            sx={{
              width: "100%",
              height: 20,
              borderRadius: "20px",
              overflow: "hidden",
              textAlign: "left",
              display: "block",
              "&:hover": {
                backgroundColor: "transparent"
              }
            }}
          >
            <Stack
              sx={{
                width: "100%",
                height: "100%",
                alignItems: "center",
                flexDirection: "row",
                gap: "2px",
                borderRadius: "20px",
                py: "2px",
                px: "4px",
                overflow: "hidden"
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "14px",
                  lineHeight: "16px",
                  fontWeight: rowData.name === "Total" || isUserModified ? 500 : 400,
                  color: isUserModified ? "#071F35" : "#92A7BC",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  ...(!rowData.type && { fontWeight: 500, color: "#071F35" })
                }}
              >
                {displayValue}
              </Typography>
              <ModeEditOutlineOutlinedIcon
                className="edit-icon"
                sx={{
                  fontSize: "16px",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#496682",
                  flexShrink: 0
                }}
              />
            </Stack>
          </ButtonBase>
        </Tooltip>
      )}
    </Stack>
  );
};
